/* eslint-disable @typescript-eslint/no-unused-vars,no-underscore-dangle */
import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { SkipPayBaseElement } from './base';
import { TranslationKey } from './translations/types';
import { Currency, Language, Product } from './types';

@customElement('sp-banner')
export class SPBanner extends SkipPayBaseElement {
	static override get styles() {
		return css`
			:host {
				display: flex;
				flex-direction: row;
				align-items: center;
				padding: 1rem;
				gap: 1rem;
				border-radius: 8px;
				border: 1px solid #1e82f0;
				background: #e8f2fd;
				cursor: pointer;
				max-width: 600px;
			}

			p {
				margin: 0;
				display: inline;
			}

			.link {
				margin-left: 0.25rem;
			}

			strong {
				font-weight: 600;
			}
		`;
	}

	@property({ type: Object })
	translations!: Record<TranslationKey, string>;

	@property({ type: String })
	product: Product = 'PAY_IN_THREE';

	@property({ type: Number, attribute: 'text-variant' })
	textVariant!: number;

	@property({ type: String })
	merchantName: string = window.location.hostname;

	@property({ type: Number })
	amount?: number;

	@property({ type: String })
	currency?: Currency;

	@property({ type: String })
	language!: Language;

	private _getBannerTemplate() {
		const { product, textVariant, amount, currency } = this;

		if (product === 'PAY_IN_THREE') {
			if (amount && currency) {
				const [firstInstallment] = this.calculateInstallments(amount);
				const formattedAmountWithCurrency = `${this.formatAmountForLocale(firstInstallment, this.language)}\u00A0${this.getCurrencySymbol(currency)}`;

				return this._formatTextWithPlaceholders(
					this.translations.PAY_IN_THREE_BANNER_WITH_AMOUNT,
					{ amount: formattedAmountWithCurrency },
					{
						amount: 'strong',
					}
				);
			}

			return html`${this.translations.PAY_IN_THREE_BANNER_1}`;
		}

		if (textVariant === 1) {
			return html`${this.translations.DEFERRED_PAYMENT_BANNER_1}`;
		}

		return html`${this.translations.DEFERRED_PAYMENT_BANNER_2}`;
	}

	private _handleClick(e: Event) {
		e.preventDefault();
	}

	private _getLogo() {
		const analyticsParams = {
			pl: 'd',
			m: this.getMerchantName(this.merchantName),
			p: this.product === 'PAY_IN_THREE' ? '3' : 'd',
		};

		const logoUrl = new URL(`${process.env.ASSETS_BASE_URL}/logo.svg`);
		logoUrl.search = new URLSearchParams(analyticsParams).toString();

		return logoUrl.toString();
	}

	override render() {
		return html`
			<img width="54" height="25" src=${this._getLogo()} alt="Skip Pay" loading="lazy" />
			<div>
				<p id="skippay-banner-title">
					${this._getBannerTemplate()}
					<a
						href="#"
						@click=${this._handleClick}
						class="link"
						aria-label=${this.translations.MORE_INFO}
						role="button"
						>${this.translations.MORE_INFO}</a
					>
				</p>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'sp-banner': SPBanner;
	}
}
